"use client"

import { useState, useEffect, useRef } from "react"
import axiosInstance from "../../../utils/axiosInstance"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Loader2, Upload, X, Stamp, PenTool, Palette } from "lucide-react"
import { useToast } from "../../../hooks/use-toast"
import { API_ENDPOINTS } from "../../../constants/config"

export function ReceiptCustomization() {
  const [loading, setLoading] = useState(true)
  const [uploadingStamp, setUploadingStamp] = useState(false)
  const [uploadingSignature, setUploadingSignature] = useState(false)
  const [schoolData, setSchoolData] = useState({
    receiptBackgroundColor: "#fef3e2",
    receiptOuterColor: "#ffffff",
    receiptInnerColor: "#fef3e2",
    receiptStamp: "",
    receiptSignature: "",
  })
  const [error, setError] = useState(null)
  const stampInputRef = useRef(null)
  const signatureInputRef = useRef(null)
  const { toast } = useToast()

  useEffect(() => {
    fetchSchoolData()
  }, [])

  const fetchSchoolData = async () => {
    try {
      setLoading(true)
      const response = await axiosInstance.get(API_ENDPOINTS.schoolAbout)
      if (response.data.status === "success") {
        const school = response.data.data.school
        setSchoolData({
          receiptBackgroundColor: school.receiptBackgroundColor || "#fef3e2",
          receiptOuterColor: school.receiptOuterColor || "#ffffff",
          receiptInnerColor: school.receiptInnerColor || "#fef3e2",
          receiptStamp: school.receiptStamp || "",
          receiptSignature: school.receiptSignature || "",
        })
      }
    } catch (error) {
      console.error("Error fetching school data:", error)
      setError(error.response?.data?.message || "Failed to fetch school data")
      toast({
        title: "Error",
        description: "Failed to load receipt customization settings",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleColorChange = async (color, colorType) => {
    try {
      const updateData = { [colorType]: color }
      const response = await axiosInstance.patch(
        API_ENDPOINTS.schoolAbout,
        updateData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      )

      if (response.data.status === "success") {
        setSchoolData({
          ...schoolData,
          [colorType]: color,
        })
        toast({
          title: "Success",
          description: "Color updated successfully!",
        })
      }
    } catch (error) {
      console.error("Error updating color:", error)
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to update color",
        variant: "destructive",
      })
    }
  }

  const handleStampUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid File",
        description: "Please upload an image file",
        variant: "destructive",
      })
      return
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "File size should be less than 5MB",
        variant: "destructive",
      })
      return
    }

    try {
      setUploadingStamp(true)
      
      // Convert file to base64
      const reader = new FileReader()
      reader.onloadend = async () => {
        const base64String = reader.result
        
        try {
          const response = await axiosInstance.post(
            API_ENDPOINTS.schoolUploadReceiptStamp,
            { stamp: base64String },
            {
              headers: {
                "Content-Type": "application/json",
              },
            }
          )

          if (response.data.status === "success") {
            setSchoolData({
              ...schoolData,
              receiptStamp: response.data.data.receiptStamp,
            })
            toast({
              title: "Success",
              description: "Receipt stamp uploaded successfully!",
            })
          }
        } catch (error) {
          console.error("Error uploading stamp:", error)
          toast({
            title: "Upload Failed",
            description: error.response?.data?.message || "Failed to upload stamp",
            variant: "destructive",
          })
        } finally {
          setUploadingStamp(false)
          if (stampInputRef.current) {
            stampInputRef.current.value = ""
          }
        }
      }
      
      reader.onerror = () => {
        toast({
          title: "Error",
          description: "Failed to read file",
          variant: "destructive",
        })
        setUploadingStamp(false)
      }
      
      reader.readAsDataURL(file)
    } catch (error) {
      console.error("Error processing stamp:", error)
      toast({
        title: "Error",
        description: "Failed to process stamp",
        variant: "destructive",
      })
      setUploadingStamp(false)
    }
  }

  const handleSignatureUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid File",
        description: "Please upload an image file",
        variant: "destructive",
      })
      return
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "File size should be less than 5MB",
        variant: "destructive",
      })
      return
    }

    try {
      setUploadingSignature(true)
      
      // Convert file to base64
      const reader = new FileReader()
      reader.onloadend = async () => {
        const base64String = reader.result
        
        try {
          const response = await axiosInstance.post(
            API_ENDPOINTS.schoolUploadReceiptSignature,
            { signature: base64String },
            {
              headers: {
                "Content-Type": "application/json",
              },
            }
          )

          if (response.data.status === "success") {
            setSchoolData({
              ...schoolData,
              receiptSignature: response.data.data.receiptSignature,
            })
            toast({
              title: "Success",
              description: "Receipt signature uploaded successfully!",
            })
          }
        } catch (error) {
          console.error("Error uploading signature:", error)
          toast({
            title: "Upload Failed",
            description: error.response?.data?.message || "Failed to upload signature",
            variant: "destructive",
          })
        } finally {
          setUploadingSignature(false)
          if (signatureInputRef.current) {
            signatureInputRef.current.value = ""
          }
        }
      }
      
      reader.onerror = () => {
        toast({
          title: "Error",
          description: "Failed to read file",
          variant: "destructive",
        })
        setUploadingSignature(false)
      }
      
      reader.readAsDataURL(file)
    } catch (error) {
      console.error("Error processing signature:", error)
      toast({
        title: "Error",
        description: "Failed to process signature",
        variant: "destructive",
      })
      setUploadingSignature(false)
    }
  }

  const handleRemoveStamp = async () => {
    try {
      const response = await axiosInstance.patch(
        API_ENDPOINTS.schoolAbout,
        { receiptStamp: "" },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      )

      if (response.data.status === "success") {
        setSchoolData({
          ...schoolData,
          receiptStamp: "",
        })
        toast({
          title: "Success",
          description: "Receipt stamp removed successfully!",
        })
      }
    } catch (error) {
      console.error("Error removing stamp:", error)
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to remove stamp",
        variant: "destructive",
      })
    }
  }

  const handleRemoveSignature = async () => {
    try {
      const response = await axiosInstance.patch(
        API_ENDPOINTS.schoolAbout,
        { receiptSignature: "" },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      )

      if (response.data.status === "success") {
        setSchoolData({
          ...schoolData,
          receiptSignature: "",
        })
        toast({
          title: "Success",
          description: "Receipt signature removed successfully!",
        })
      }
    } catch (error) {
      console.error("Error removing signature:", error)
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to remove signature",
        variant: "destructive",
      })
    }
  }

  // Predefined color options
  const colorOptions = [
    { name: "Beige", value: "#fef3e2" },
    { name: "White", value: "#ffffff" },
    { name: "Light Blue", value: "#e0f2fe" },
    { name: "Light Gray", value: "#f3f4f6" },
    { name: "Cream", value: "#fff7ed" },
    { name: "Light Green", value: "#f0fdf4" },
    { name: "Light Pink", value: "#fdf2f8" },
    { name: "Light Yellow", value: "#fefce8" },
  ]

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#0263cc]" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5 text-[#0263cc]" />
            Fee Receipt Customization
          </CardTitle>
          <CardDescription>
            Customize receipt colors (outer layer for header/signature, inner layer for details), center stamp, and signature for all fee receipts
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Outer Layer Color Selection (Header & Signature) */}
          <div className="space-y-4">
            <Label className="text-base font-semibold">Outer Layer Color (Header & Signature Section)</Label>
            <p className="text-xs text-muted-foreground">This color will be used for school header and authorized signature section</p>
            <div className="p-6 border border-dashed rounded-lg border-[#0263cc]/30 bg-[#0263cc]/5">
              <div className="grid grid-cols-4 sm:grid-cols-4 gap-3 mb-4">
                {colorOptions.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => handleColorChange(color.value, 'receiptOuterColor')}
                    className={`relative h-12 w-full rounded-lg border-2 transition-all ${
                      schoolData.receiptOuterColor === color.value
                        ? "border-[#0263cc] ring-2 ring-[#0263cc]/20 scale-105"
                        : "border-gray-300 hover:border-[#0263cc]/50"
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                  >
                    {schoolData.receiptOuterColor === color.value && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="h-3 w-3 rounded-full bg-[#0263cc]"></div>
                      </div>
                    )}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <Label htmlFor="outer-color" className="text-sm font-medium">
                  Custom Color:
                </Label>
                <Input
                  id="outer-color"
                  type="color"
                  value={schoolData.receiptOuterColor}
                  onChange={(e) => handleColorChange(e.target.value, 'receiptOuterColor')}
                  className="h-10 w-20 cursor-pointer"
                />
                <span className="text-sm text-muted-foreground">
                  {schoolData.receiptOuterColor}
                </span>
              </div>
            </div>
          </div>

          {/* Inner Layer Color Selection (Receipt Details) */}
          <div className="space-y-4">
            <Label className="text-base font-semibold">Inner Layer Color (Receipt Details Section)</Label>
            <p className="text-xs text-muted-foreground">This color will be used for receipt number, student name, fee breakdown, etc.</p>
            <div className="p-6 border border-dashed rounded-lg border-[#0263cc]/30 bg-[#0263cc]/5">
              <div className="grid grid-cols-4 sm:grid-cols-4 gap-3 mb-4">
                {colorOptions.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => handleColorChange(color.value, 'receiptInnerColor')}
                    className={`relative h-12 w-full rounded-lg border-2 transition-all ${
                      schoolData.receiptInnerColor === color.value
                        ? "border-[#0263cc] ring-2 ring-[#0263cc]/20 scale-105"
                        : "border-gray-300 hover:border-[#0263cc]/50"
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                  >
                    {schoolData.receiptInnerColor === color.value && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="h-3 w-3 rounded-full bg-[#0263cc]"></div>
                      </div>
                    )}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <Label htmlFor="inner-color" className="text-sm font-medium">
                  Custom Color:
                </Label>
                <Input
                  id="inner-color"
                  type="color"
                  value={schoolData.receiptInnerColor}
                  onChange={(e) => handleColorChange(e.target.value, 'receiptInnerColor')}
                  className="h-10 w-20 cursor-pointer"
                />
                <span className="text-sm text-muted-foreground">
                  {schoolData.receiptInnerColor}
                </span>
              </div>
            </div>
          </div>

          {/* Receipt Stamp Upload (Center) */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">Receipt Stamp (Center)</Label>
            </div>
            <div className="flex flex-col sm:flex-row items-start gap-4 p-6 border border-dashed rounded-lg border-[#0263cc]/30 bg-[#0263cc]/5">
              {schoolData.receiptStamp ? (
                <div className="relative">
                  <img
                    src={schoolData.receiptStamp}
                    alt="Receipt Stamp"
                    className="h-32 w-auto object-contain rounded-lg border bg-white p-2 shadow-sm"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute -top-2 -right-2 h-8 w-8 rounded-full"
                    onClick={handleRemoveStamp}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="h-32 w-32 flex items-center justify-center border rounded-lg bg-white text-[#0263cc]">
                  <Stamp className="h-10 w-10 opacity-70" />
                </div>
              )}

              <div className="flex-1 space-y-2">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleStampUpload}
                  className="hidden"
                  id="stamp-upload"
                  ref={stampInputRef}
                  disabled={uploadingStamp}
                />
                <Label
                  htmlFor="stamp-upload"
                  className="cursor-pointer block"
                >
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-[#0263cc]/30 text-[#0263cc] hover:bg-[#0263cc]/10"
                    disabled={uploadingStamp}
                    onClick={(e) => {
                      e.preventDefault()
                      document.getElementById("stamp-upload").click()
                    }}
                  >
                    {uploadingStamp ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        {schoolData.receiptStamp ? "Change Stamp" : "Upload Stamp"}
                      </>
                    )}
                  </Button>
                </Label>
                <p className="text-xs text-muted-foreground">
                  Upload a stamp image. This will appear in the center of all fee receipts.
                  Recommended size: 200x200px or similar. Max size: 5MB.
                </p>
              </div>
            </div>
          </div>

          {/* Receipt Signature Upload */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">Receipt Signature</Label>
            </div>
            <div className="flex flex-col sm:flex-row items-start gap-4 p-6 border border-dashed rounded-lg border-[#0263cc]/30 bg-[#0263cc]/5">
              {schoolData.receiptSignature ? (
                <div className="relative">
                  <img
                    src={schoolData.receiptSignature}
                    alt="Receipt Signature"
                    className="h-24 w-auto object-contain rounded-lg border bg-white p-2 shadow-sm"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute -top-2 -right-2 h-8 w-8 rounded-full"
                    onClick={handleRemoveSignature}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="h-24 w-32 flex items-center justify-center border rounded-lg bg-white text-[#0263cc]">
                  <PenTool className="h-8 w-8 opacity-70" />
                </div>
              )}

              <div className="flex-1 space-y-2">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleSignatureUpload}
                  className="hidden"
                  id="signature-upload"
                  ref={signatureInputRef}
                  disabled={uploadingSignature}
                />
                <Label
                  htmlFor="signature-upload"
                  className="cursor-pointer block"
                >
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-[#0263cc]/30 text-[#0263cc] hover:bg-[#0263cc]/10"
                    disabled={uploadingSignature}
                    onClick={(e) => {
                      e.preventDefault()
                      document.getElementById("signature-upload").click()
                    }}
                  >
                    {uploadingSignature ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        {schoolData.receiptSignature ? "Change Signature" : "Upload Signature"}
                      </>
                    )}
                  </Button>
                </Label>
                <p className="text-xs text-muted-foreground">
                  Upload a signature image. This will appear in the signature section of all fee receipts.
                  Recommended size: 150x60px or similar. Max size: 5MB.
                </p>
              </div>
            </div>
          </div>

          <Alert>
            <Palette className="h-4 w-4" />
            <AlertDescription>
              <strong>Note:</strong> Outer layer color applies to header (school name, address) and signature section. 
              Inner layer color applies to receipt details (receipt number, student name, fee breakdown). 
              The stamp will be displayed as a watermark in the center, and the signature will appear in the signature section.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  )
}
